import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { Check as FollowingIcon, HowToReg as ConnectedIcon, PersonAdd as FollowIcon, PersonAddAlt1 as ConnectIcon, PersonRemove as UnfollowIcon,
  Schedule as RequestedIcon } from '@mui/icons-material';
import useFollowActions from '../../../shared/hooks/useFollowActions';
import { useFollowCache } from '../../../shared/contexts/FollowCacheContext';
import { fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { answerRequest, askToFollow, refreshFollows, removeConnection, stateUrl } from 'components/social/connections/connectionsApi';

const iconOnlySx = { minWidth: 28, width: 28, height: 28, p: 0, flexShrink: 0 };
const withTextSx = { minWidth: 'auto', '& .MuiButton-startIcon': { marginRight: 0.5 } };

/**
 * Connecting with a person: Connect sends a request, "Requested" waits for their answer (click to cancel it),
 * "Accept" answers theirs, and "Connected" once accepted (click to remove the connection, for both). People are
 * connected with, everything else is followed. Only "Connected" may shrink to an icon (COMPACT):
 * Follow and Requested show their text, unless COMPACT_ALL (the button sits among action buttons, as for staff
 * in the Collaboration Hub). STATE: the page's batch of states, when it has one
 * (each row's follow_state in /users/, or /interact/follow-requests/states/); otherwise the button asks for
 * its own. After a click the button keeps the server's answer until the page's STATE changes.
 */
function PersonFollowButton({ objectId, size, variant, compact, compactAll, sx, state }) {
  const { followCache, updateFollowCache } = useFollowCache();
  const { data: fetched } = useSWR(objectId && state === undefined ? stateUrl(objectId) : null, fetcher);
  const { showSnackbar } = useSnackbar() || {};
  const [optimistic, setOptimistic] = useState(null);
  const [loading, setLoading] = useState(false);
  // fresh data from the page (or its own fetch) replaces what the last click left
  useEffect(() => { setOptimistic(null); }, [state?.state, fetched?.state]);
  const data = optimistic || state || fetched;
  if (!data || data.state === 'self') return null;

  const run = async (next, fn) => {
    if (loading) return;
    setOptimistic(next);
    setLoading(true);
    try {
      const res = await fn();
      // keep the answer (a new request's id, so it can be withdrawn at once)
      if (res?.state === 'requested' || res?.state === 'connected') setOptimistic(res);
      else if (res?.state === 'accepted') setOptimistic({ state: 'connected' });
    } catch (error) {
      // a refusal says why: asked too often today, withdrawn or declined recently…
      showSnackbar?.(apiErrorMessage(error, 'That did not work. Please try again.'), 'error');
      setOptimistic(null);
    } finally {
      setLoading(false);
      refreshFollows();
    }
  };
  const onClick = (e) => {
    e.stopPropagation();
    if (data.state === 'connected') {
      if (!window.confirm('Remove this connection? Your profiles close to each other again.')) return;
      run({ state: 'none' }, async () => {
        await removeConnection(objectId);
        if (followCache !== undefined) updateFollowCache('user', objectId, false);
      });
    } else if (data.state === 'received') {
      run({ state: 'connected' }, () => answerRequest(data.request_id, 'accept'));
    } else if (data.state === 'requested') {
      run({ state: 'none' }, () => (data.request_id ? answerRequest(data.request_id, 'withdraw') : Promise.resolve()));
    } else {
      run({ state: 'requested' }, () => askToFollow(objectId));
    }
  };

  const look = {
    connected: ['Connected', <ConnectedIcon />, 'Connected. Click to remove the connection'],
    requested: ['Requested', <RequestedIcon />, 'Waiting for them to accept. Click to cancel the request'],
    received: ['Accept', <ConnectedIcon />, 'They asked to connect with you: accept'],
    none: ['Connect', <ConnectIcon />, 'Ask to connect: once they accept, you see each other\'s whole profile'],
  }[data.state] || ['Connect', <ConnectIcon />, ''];
  const iconOnly = compact && (data.state === 'connected' || compactAll);
  const icon = loading ? <CircularProgress size={iconOnly ? 14 : 16} /> : look[1];

  if (iconOnly) {
    return (
      <Tooltip title={look[2]} placement="left">
        <span>
          <Button size={size} variant={['none', 'received'].includes(data.state) ? 'contained' : variant} disableElevation onClick={onClick}
            disabled={loading} aria-label={look[0]} sx={{ ...iconOnlySx, ...sx }}>
            {icon}
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={look[2]}>
      <span>
        <Button size={size} variant={['none', 'received'].includes(data.state) ? 'contained' : variant} disableElevation onClick={onClick}
          disabled={loading} startIcon={icon} sx={{ ...withTextSx, ...sx }}>
          {look[0]}
        </Button>
      </span>
    </Tooltip>
  );
}

/** Organisations, consortia, calls, labs…: followed at once. */
function EntityFollowButton({ contentType, objectId, size, variant, compact, sx }) {
  const { followEntity, unfollowEntity } = useFollowActions();
  const { followCache, followCacheLoading, isFollowing, updateFollowCache } = useFollowCache();
  const [loading, setLoading] = useState(false);
  const [optimisticFollowing, setOptimisticFollowing] = useState(null);

  const normalizedContentType = contentType?.toString().toLowerCase();
  const isCacheReady = followCache !== undefined;
  const following = optimisticFollowing !== null
    ? optimisticFollowing
    : (isCacheReady ? isFollowing(normalizedContentType, objectId) : false);

  const syncCache = (nextState) => {
    if (!isCacheReady) return;
    updateFollowCache(normalizedContentType, objectId, nextState);
  };

  const handleToggleFollow = async () => {
    if (loading) return;
    const nextFollowing = !following;
    setOptimisticFollowing(nextFollowing);
    setLoading(true);
    syncCache(nextFollowing);
    try {
      if (following) {
        await unfollowEntity(normalizedContentType, objectId);
      } else {
        await followEntity(normalizedContentType, objectId);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      setOptimisticFollowing(!nextFollowing);
      syncCache(!nextFollowing);
    } finally {
      setLoading(false);
    }
  };

  const label = followCacheLoading && !isCacheReady ? '…' : (following ? 'Following' : 'Follow');
  const tooltipTitle = loading ? '' : (following ? 'Following — click to unfollow' : 'Follow');

  if (compact) {
    return (
      <Tooltip title={tooltipTitle} placement="left">
        <span>
          <Button size={size} variant={following ? variant : 'contained'} disabled={loading}
            onClick={(e) => { e.stopPropagation(); handleToggleFollow(); }} sx={{ ...iconOnlySx, ...sx }}>
            {/* followed: a check (a minus sign read as "follow"); unfollowing is the click */}
            {loading ? <CircularProgress size={14} /> : following ? <FollowingIcon fontSize="small" /> : <FollowIcon fontSize="medium" />}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button size={size} variant={variant} disabled={loading}
      onClick={(e) => { e.stopPropagation(); handleToggleFollow(); }}
      startIcon={loading ? <CircularProgress size={16} /> : following ? <UnfollowIcon /> : <FollowIcon />}
      sx={{ ...withTextSx, ...sx }}>
      {label}
    </Button>
  );
}

const FollowButton = ({ contentType, objectId, size = 'small', variant = 'outlined', compact = false, compactAll = false, sx = {}, state }) => {
  const props = { contentType, objectId, size, variant, compact, compactAll, sx, state };
  return contentType?.toString().toLowerCase() === 'user' ? <PersonFollowButton {...props} /> : <EntityFollowButton {...props} />;
};

export default FollowButton;
